import Profile from '../profile/Profile';
import TransactionMonitoring from '../transaction-monitoring/TransactionMonitoring';
import TransactionDashboard from '../transaction-dashboard/TransactionDashboard';
import UserManagement from '../user-management/UserManagement';
import Permissions from '../permissions/Permissions';
import Companies from '../companies/Companies';
import Terminals from '../terminals/Terminals';
import CreateTerminal from '../terminals/components/create-terminal/CreateTerminal';
import CreateMerchant from '../merchants/components/CreateMerchant';
import Merchants from '../merchants/Merchants';
import Developers from '../developers/Developers';
import Documentation from '../api-documentation/Documentation';
import DccMarkupRates from '../dcc-markup-rates/DccMarkupRates';
import StorefrontUrls from '../storefront-urls/StorefrontUrls';
import StorefrontUrlReview from '../storefront-url-review/StorefrontUrlReview';
import PaymentRouting from '../payment-routing/PaymentRouting';
import LogsAudit from '../logs-audit/LogsAudit';
import ErrorCodesMapping from '../error-codes-mapping/ErrorCodesMapping';
import FraudMonitoring from '../fraud/FraudMonitoring';
import RiskRules from '../risk-rules/RiskRules';
import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import MerchantDashboardLayout from './merchant/MerchantDashboardLayout';
import { useAuthStore } from '../../store/authStore';
import { apiService } from '../../services/apiService';
import { API_PATHS } from '../../utils/apiPaths';
import { isLightningMerchantDashboard, resolveMerchantDashboardKey } from '../../utils/merchantDashboardKey';
import Acquirers from '../acquirers/Acquirers';
import CreateAcquirer from '../acquirers/components/CreateAcquirer';
import CreateOrder from '../transaction-monitoring/components/create-order/CreateOrder';
import './Dashboard.css';
import '../finance/Finance.css';
import Roles from '../roles/Roles';
import { RequireModule } from './components/RequireModule';

const Dashboard: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const useLightningShell = isLightningMerchantDashboard(user);
  const Layout = useLightningShell ? MerchantDashboardLayout : DashboardLayout;

  useEffect(() => {
    if (user?.userType !== 'MERCHANT' || !user.merchantId) return;
    const merchantId = user.merchantId;

    let cancelled = false;
    const loadDashboardKey = async () => {
      try {
        const res = await apiService.get(`${API_PATHS.GET_MERCHANT}/${merchantId}`);
        if (cancelled) return;
        const dashboardKey = resolveMerchantDashboardKey(res);
        const current = useAuthStore.getState();
        if (!current.user || dashboardKey === current.user.dashboardKey) return;
        current.login({ ...current.user, dashboardKey }, current.token ?? '');
      } catch (error) {
        console.error('Failed to load merchant dashboard key:', error);
      }
    };

    void loadDashboardKey();
    return () => {
      cancelled = true;
    };
  }, [user?.userType, user?.merchantId]);

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard/transaction-dashboard" replace />} />
        <Route path="/transaction-dashboard" element={<RequireModule path="/transaction-dashboard"><TransactionDashboard /></RequireModule>} />
        <Route path="/acquirers" element={<RequireModule path="/acquirers"><Acquirers /></RequireModule>} />
        <Route path="/acquirers/create" element={<RequireModule path="/acquirers/create"><CreateAcquirer /></RequireModule>} />
        <Route path="/acquirers/edit/:id" element={<RequireModule path="/acquirers/edit/:id"><CreateAcquirer /></RequireModule>} />
        <Route path="/merchants/manage" element={<RequireModule path="/merchants/manage"><Merchants /></RequireModule>} />
        <Route path="/merchants/create" element={<RequireModule path="/merchants/create"><CreateMerchant /></RequireModule>} />
        <Route path="/merchants/edit/:id" element={<RequireModule path="/merchants/edit/:id"><CreateMerchant /></RequireModule>} />
        <Route path="/terminals" element={<RequireModule path="/terminals"><Terminals /></RequireModule>} />
        <Route path="/terminals/create" element={<RequireModule path="/terminals/create"><CreateTerminal /></RequireModule>} />
        <Route path="/transaction-monitoring" element={<RequireModule path="/transaction-monitoring"><TransactionMonitoring /></RequireModule>} />
        <Route path="/profile" element={<RequireModule path="/profile"><Profile /></RequireModule>} />
        <Route path="/user-management" element={<RequireModule path="/user-management"><UserManagement /></RequireModule>} />
        <Route path="/permissions" element={<RequireModule path="/permissions"><Permissions /></RequireModule>} />
        <Route path="/documentation" element={<RequireModule path="/documentation"><Documentation /></RequireModule>} />
        <Route path="/storefront-urls" element={<RequireModule path="/storefront-urls"><StorefrontUrls /></RequireModule>} />
        <Route path="/storefront-url-review" element={<RequireModule path="/storefront-url-review"><StorefrontUrlReview /></RequireModule>} />
        <Route path="/companies" element={<RequireModule path="/companies"><Companies /></RequireModule>} />
        <Route path="/roles" element={<RequireModule path="/roles"><Roles /></RequireModule>} />
        <Route path="/developers" element={<RequireModule path="/developers"><Developers /></RequireModule>} />
        <Route path="/dcc-markup-rates" element={<RequireModule path="/dcc-markup-rates"><DccMarkupRates /></RequireModule>} />
        <Route path="/payment-routing" element={<RequireModule path="/payment-routing"><PaymentRouting /></RequireModule>} />
        <Route path="/logs" element={<RequireModule path="/logs"><LogsAudit /></RequireModule>} />
        <Route path="/error-codes-mapping" element={<RequireModule path="/error-codes-mapping"><ErrorCodesMapping /></RequireModule>} />
        <Route path="/fraud" element={<RequireModule path="/fraud"><FraudMonitoring /></RequireModule>} />
        <Route path="/risk-rules" element={<RequireModule path="/risk-rules"><RiskRules /></RequireModule>} />
        <Route path="/orders/create" element={<RequireModule path="/orders/create"><CreateOrder /></RequireModule>} />
        <Route path="/transactions" element={<RequireModule path="/transactions"><div className="p-4">Transactions Page - Coming Soon</div></RequireModule>} />
        <Route path="/users" element={<RequireModule path="/users"><div className="p-4">Users Page - Coming Soon</div></RequireModule>} />
        <Route path="/settings" element={<RequireModule path="/settings"><div className="p-4">Settings Page - Coming Soon</div></RequireModule>} />
      </Routes>
    </Layout>
  );
};

export default Dashboard;
